import type { Rule } from '@signetix/rule-engine'
import { useState } from 'react'
import { toast } from 'sonner'
import { notifyError } from '@/components/notify'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api'
import type { LibraryRule, RuleValidation } from '@/types/api'
import { useCreateRule, useUpdateRule, usageCount, usageText } from '../api'
import { ConditionBuilder } from './condition-builder'

/**
 * Create or edit a saved condition (library rule). Edits apply to every campaign, schedule and
 * channel item that uses it.
 */
export function RuleDialog({
  open,
  onOpenChange,
  rule,
  defaultName,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Existing rule to edit; create otherwise. */
  rule?: LibraryRule | null
  defaultName?: string
  onSaved?: (rule: LibraryRule) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {open && (
          <RuleForm
            key={rule?.id ?? 'new'}
            rule={rule ?? null}
            defaultName={defaultName}
            onCancel={() => onOpenChange(false)}
            onSaved={(r) => {
              onOpenChange(false)
              onSaved?.(r)
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function RuleForm({ rule, defaultName, onSaved, onCancel }: { rule: LibraryRule | null; defaultName?: string; onSaved: (r: LibraryRule) => void; onCancel: () => void }) {
  const create = useCreateRule()
  const update = useUpdateRule()
  const [name, setName] = useState(rule?.name ?? defaultName ?? '')
  const [description, setDescription] = useState(rule?.description ?? '')
  const [doc, setDoc] = useState<Rule | null>((rule?.rule as Rule | undefined) ?? null)
  const [validation, setValidation] = useState<RuleValidation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const pending = create.isPending || update.isPending
  const others = rule ? usageCount(rule) : 0

  const save = async () => {
    setError(null)
    if (!name.trim()) return setNameError('Give the condition a name')
    if (!doc) return setError('Finish every condition first.')
    const input = { name: name.trim().slice(0, 160), description: description.trim() || null, rule: doc as unknown as Record<string, unknown> }
    try {
      const saved = rule ? await update.mutateAsync({ id: rule.id, ...input }) : await create.mutateAsync(input)
      toast.success(rule ? 'Condition updated' : 'Condition saved', { description: saved.name })
      onSaved(saved)
    } catch (e) {
      if (isApiError(e) && Array.isArray(e.problem.errors)) {
        // 422 `rule-invalid`: `errors: [{path, code, message}]` (the builder shows the same via /rules/validate).
        const list = e.problem.errors as unknown as { message?: string }[]
        setError(list.map((x) => x.message).filter(Boolean)[0] ?? e.detail)
      } else if (isApiError(e) && e.isValidation) {
        if (e.fieldError('name')) setNameError(e.fieldError('name') ?? null)
        setError(Object.entries(e.errors).filter(([k]) => k !== 'name').map(([, m]) => m[0])[0] ?? (e.fieldError('name') ? null : e.detail))
      } else notifyError(e)
    }
  }

  return (
    <form
      className="grid min-w-0 gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
    >
      <DialogHeader>
        <DialogTitle>{rule ? 'Edit condition' : 'New condition'}</DialogTitle>
        <DialogDescription>Saved conditions can be reused by campaigns, schedules and channel items. Content only plays while its condition holds.</DialogDescription>
      </DialogHeader>
      <FormError message={error ?? undefined} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required error={nameError ?? undefined}>
          <Input
            autoFocus
            value={name}
            maxLength={160}
            placeholder="e.g. Hot days in Serbia"
            onChange={(e) => {
              setName(e.target.value)
              setNameError(null)
            }}
          />
        </Field>
        <Field label="Description" optional>
          <Textarea rows={1} className="min-h-9" value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <ConditionBuilder value={rule?.rule ?? null} onChange={(c) => setDoc(c.rule)} onValidation={setValidation} />
      {rule && others > 0 && <p className="rounded-md bg-info/8 px-3 py-2 text-[12.5px]">Used by {usageText(rule)} — your changes apply to all of them.</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={pending} disabled={!doc || (validation ? !validation.valid : false)}>
          {rule ? 'Save changes' : 'Save condition'}
        </Button>
      </DialogFooter>
    </form>
  )
}
